import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, CreateRecordProperties, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { getFormattedTime } from 'webapp/utils/number';
import type { ValidationRule } from 'webapp/utils/validate';
import type { VerificationInfo } from 'webapp/utils/verification';

import type { CustomQueryRecord, SourceRecord, TagRecord, UserRecord } from 'webapp/models';
import type { Metric, Source, SourceType } from 'webapp/models-custom';
import BufferedModel from 'webapp/models-custom/buffer/buffered-model';
import MetricSetup from 'webapp/models-custom/metric-setup';
import type { StatusObjectAction } from 'webapp/models-custom/status-object';
import StatusObject, { StatusObjectType } from 'webapp/models-custom/status-object';

import { Severity } from 'webapp/services/logger';

export interface DefaultTitleInfo {
	metric?: string;
	dimension?: string;
}

export default class CustomQuery extends BufferedModel<
	CustomQueryRecord,
	RecordData.CustomQuery.Serialized
> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('connect') declare connectService: Service.Connect;
	@service('logger') declare loggerService: Service.Logger;
	@service('model-manager/source-types')
	declare mmSourceTypesService: Service.ModelManager.SourceTypes;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/tags') declare mmTagsService: Service.ModelManager.Tags;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;

	// ------- props from / to ED record ------

	get id(): string | null {
		return this.model?.id || null;
	}

	get queryName(): string {
		return this.getBuffered('queryName');
	}

	set queryName(value: string) {
		this.setBuffered('queryName', value);
	}

	get created(): number {
		return this.model.created;
	}

	get createdById(): number {
		return this.model.createdById;
	}

	get modified(): number | null {
		return this.model.modified;
	}

	get modifiedById(): number | null {
		return this.model.modifiedById;
	}

	get verificationInfo(): VerificationInfo | undefined {
		return this.metric?.verificationInfo;
	}

	get sourceId(): number {
		return this.getBuffered('sourceId');
	}

	set sourceId(value: number) {
		this.setBuffered('sourceId', value);
	}

	get sourceToId(): number {
		return this.getBuffered('sourceToId');
	}

	set sourceToId(value: number) {
		this.setBuffered('sourceToId', value);
	}

	get statusLevel(): string {
		return this.model.statusLevel;
	}

	get statusTitle(): string {
		return this.model.statusTitle;
	}

	get statusDescription(): string {
		return this.model.statusDescription;
	}

	get statusAction(): StatusObjectAction | null {
		return this.model.statusAction;
	}

	get statusType(): StatusObjectType | null {
		return this.model.statusType;
	}

	get statusUserResponseLink(): string {
		return this.model.statusUserResponseLink;
	}

	get settings(): APIData.CustomQuery.SettingsData {
		return this.getBuffered('settings');
	}

	set settings(value: APIData.CustomQuery.SettingsData) {
		this.setBuffered('settings', value);
	}

	get transformations(): APIData.CustomQuery.TransformationsData {
		return this.getBuffered('transformations');
	}

	set transformations(value: APIData.CustomQuery.TransformationsData) {
		this.setBuffered('transformations', value);
	}

	get cloneFrom(): number | null {
		return this.getBuffered('cloneFrom');
	}

	set cloneFrom(value: number | null) {
		this.setBuffered('cloneFrom', value);
	}

	get tags(): number[] {
		return this.getBuffered('tags');
	}

	set tags(value: number[]) {
		this.setBuffered('tags', value);
	}

	get originateFrom(): number | null {
		return this.model.originateFrom || null;
	}

	get migrateData(): boolean {
		return this.getBuffered('migrateData');
	}

	set migrateData(value: boolean) {
		this.setBuffered('migrateData', value);
	}

	get preferredVisualization(): number {
		return this.getBuffered('preferredVisualization');
	}

	set preferredVisualization(value: number) {
		this.setBuffered('preferredVisualization', value);
	}

	set defaultTitle(value: DefaultTitleInfo | null) {
		this.setBuffered('defaultTitle', value);
	}

	get isFromPublicTemplate(): boolean {
		return this.model.isFromPublicTemplate;
	}

	get isMine(): boolean {
		return this.createdById === this.currentUserService.numericId;
	}

	// ------- other props ------

	override get validationRules(): Record<string, ValidationRule[]> {
		return {};
	}

	get createdBy(): UserRecord | null {
		let user;

		if (this.createdById) {
			user = this.mmUsersService.findRecord(String(this.createdById));
			if (!user) {
				this.loggerService.logError(
					'Missing createdById for customQuery',
					{
						queryId: this.id,
						userId: this.createdById,
					},
					Severity.WARNING,
				);
			}
		}

		return user || null;
	}

	get source(): SourceRecord | null {
		let source;

		if (this.sourceId) {
			source = this.mmSourcesService.findRecord(String(this.sourceId));

			if (!source) {
				// disable logging until this is fixed
				// https://app.asana.com/0/188486158010832/1203647088933472/f
				// this.loggerService.logError('Missing source for customQuery', {
				// 	customQueryId: this.id,
				// 	sourceId: this.sourceId
				// }, Severity.WARNING);
			}
		}

		return source || null;
	}

	get sourceAvatar(): { name?: string; avatar: string } | null {
		if (this.sourceId) {
			if (this.isMergedDataset) {
				return {
					avatar: '/images/datasets/merge-icon.svg',
				};
			}

			if (this.isIngestDataset && this.source) {
				return {
					name: this.source.title,
					avatar: this.source.avatar,
				};
			}

			if (this.sourceInfo) {
				return {
					name: this.sourceInfo.name,
					avatar: this.sourceInfo.avatar,
				};
			}
		}

		return null;
	}

	_sourceObj: Source | null = null;
	get sourceObj(): Source | null {
		const model = this.source;

		if (!this._sourceObj || model?.id !== this._sourceObj?.id) {
			this._sourceObj = model ? this.mmSourcesService.createModelFromRecord(model) : null;
		}

		return this._sourceObj;
	}

	get sourceTo(): SourceRecord | null {
		let source = null;

		if (this.sourceToId) {
			source = this.mmSourcesService.findRecord(String(this.sourceToId));

			if (!source) {
				this.loggerService.logError(
					'Missing sourceTo for customQuery',
					{
						customQueryId: this.id,
						sourceToId: this.sourceToId,
					},
					Severity.WARNING,
				);
			}
		}

		return source;
	}

	get sourceInfo(): SourceType | null {
		let sourceType;

		const sourceTypeId = this.source?.type;
		if (sourceTypeId) {
			sourceType = this.mmSourceTypesService.findModelById(sourceTypeId);
		}

		return sourceType || null;
	}

	@cached
	get statusObject(): StatusObject | null {
		return StatusObject.createFromCustomQuery(this);
	}

	get showStatus(): boolean {
		return !!this.statusObject?.isStatusActionable;
	}

	get createdText(): string {
		return `${this.createdTimeText}<br> by ${this.createdByText}`;
	}

	get baseUrl(): string {
		return `/customqueries/${this.id}`;
	}

	@tracked builderId = '';

	get builderUrl(): string {
		const builderId = this.builderId || this.source?.id;

		return `${this.baseUrl}/builder/${builderId}`;
	}

	get canEdit(): boolean {
		return (
			this.currentUserService.isAdmin ||
			String(this.createdById) === this.currentUserService.id
		);
	}

	get isSQL(): boolean {
		return !!this.sourceInfo?.isSqlConnector;
	}

	get isDataset(): boolean {
		return !!this.sourceObj?.isDataset;
	}

	get isMergedDataset(): boolean {
		return !!this.sourceObj?.isMergedDataset;
	}

	get isTypeOfDataset(): boolean {
		return !!this.sourceObj?.isTypeOfDataset;
	}

	get isIngestDataset(): boolean {
		return !!this.sourceObj?.isTypeIngest;
	}

	get isSQLish(): boolean {
		return !!this.sourceInfo?.isSqlishConnector;
	}

	get isSheets(): boolean {
		return !!this.sourceInfo?.isSheets;
	}

	get isApiMetricBuilder(): boolean {
		return !!this.sourceInfo?.metricBuilderIntegration;
	}

	get isWizardPossible(): boolean {
		return !!this.sourceInfo?.isCustomMetricWizardPossible && !this.sourceObj?.isTypeOfDataset;
	}

	get tagsCount(): number {
		return this.tags.length;
	}

	get tagsObjs(): TagRecord[] {
		const tagIds = this.tags || [];

		return this.mmTagsService.savedRecords.filter((tag) => {
			return tagIds.includes(+tag.id!);
		});
	}

	@cached
	get metric(): Metric | null {
		let metric;
		if (this.numericId) {
			metric = this.mmMetricsService.getMetricsForQueryId(this.numericId)[0];
		}
		return metric || null;
	}

	@cached
	get metricSetup() {
		return new MetricSetup(this.metric?.metricData || undefined);
	}

	get isFavorite(): boolean {
		return !!this.metric?.isFavorite;
	}

	get statusDescriptionWithLinks(): string | null {
		return this.statusObject?.descriptionWithLinks || null;
	}

	getDataForClone(): CreateRecordProperties<CustomQueryRecord> {
		const queryData = this.toJSON();

		queryData.cloneFrom = this.numericId;
		queryData.queryName = `Copy of ${queryData.queryName}`;

		delete queryData.created;
		delete queryData.createdById;
		delete queryData.statusLevel;
		delete queryData.statusTitle;
		delete queryData.statusDescription;
		delete queryData.statusAction;
		delete queryData.statusType;
		delete queryData.statusUserResponseLink;

		return queryData;
	}

	// ----------- Common with custom-models/script -----------
	// used for filter-sort and list view for custom metrics

	get metricName(): string {
		return this.queryName;
	}

	set metricName(value: string) {
		this.queryName = value;
	}

	@cached
	get usedSources(): Source[] {
		return this.sourceObj ? [this.sourceObj] : [];
	}

	get usedSourceNames(): string {
		return this.usedSources.map((source) => source.title).join(' ') || '';
	}

	get usedSourceTypeKeys(): string {
		return this.usedSources.map((source) => source.sourceType?.key).join(' ') || '';
	}

	get createdByText(): string {
		return this.createdBy?.name || '/';
	}

	get createdTimeText(): string {
		return getFormattedTime(this.created, this.currentUserService.model?.timezone);
	}

	get modifiedBy(): UserRecord | null {
		if (this.modifiedById) {
			return this.mmUsersService.findRecord(String(this.modifiedById)) || null;
		}
		return null;
	}

	get modifiedByText(): string {
		return this.modifiedBy?.name || '/';
	}

	get modifiedTimeText(): string | null {
		return this.model.modified
			? getFormattedTime(this.model.modified, this.currentUserService.model?.timezone)
			: null;
	}

	get usedMetricsText(): string {
		return '';
	}

	// ----------- Static -----------

	static override modelName = ModelName.CUSTOM_QUERY; // store record type

	// ----------- Tracking -----------

	get trackingWizardType(): string {
		let trackingType = 'standard';

		if (this.isSheets) {
			trackingType = 'sheets_wizard';
		} else if (this.isApiMetricBuilder) {
			trackingType = 'custom_API_wizard';
		}

		return trackingType;
	}
}
